export interface ContinuationTarget {
	listFilters?: Record<string, string>;
	targetId: string;
	targetKind: string;
}

export type ContinuationSourcePhase = "ACTIVE" | "SUSPENDED" | "RESUMING" | "CLOSED";

export type ContinuationFramePhase = ContinuationSourcePhase | "DEPARTED_TO_WORKSPACE";

export type ContinuationReceiverPhase = "PENDING_NAVIGATION" | "INSPECTING";

export type ContinuationEvent =
	| "PREPARE_SOURCE"
	| "ABANDON_PREPARATION"
	| "SUSPEND_SOURCE"
	| "CONSUME_PENDING_NAVIGATION"
	| "DEPART_TO_WORKSPACE"
	| "CLAIM_RESUME"
	| "ACKNOWLEDGE_RESTORED_HOST"
	| "CLOSE_SOURCE";

export interface ContinuationPhases {
	receiverPhase: ContinuationReceiverPhase | null;
	sourcePhase: ContinuationFramePhase;
}

const RETAIN = "RETAIN";

type Retained = typeof RETAIN;

interface TransitionRule {
	fromReceiver: (ContinuationReceiverPhase | null)[];
	fromSource: ContinuationFramePhase[];
	toReceiver: ContinuationReceiverPhase | Retained | null;
	toSource: ContinuationFramePhase | Retained;
}

const CONTINUATION_TRANSITIONS: Record<ContinuationEvent, TransitionRule> = {
	ABANDON_PREPARATION: {
		fromReceiver: [null],
		fromSource: ["ACTIVE"],
		toReceiver: null,
		toSource: "ACTIVE"
	},
	ACKNOWLEDGE_RESTORED_HOST: {
		fromReceiver: [null, "PENDING_NAVIGATION", "INSPECTING"],
		fromSource: ["RESUMING"],
		toReceiver: null,
		toSource: "ACTIVE"
	},
	CLAIM_RESUME: {
		fromReceiver: [null, "PENDING_NAVIGATION", "INSPECTING"],
		fromSource: ["SUSPENDED", "RESUMING", "DEPARTED_TO_WORKSPACE"],
		toReceiver: RETAIN,
		toSource: "RESUMING"
	},
	CLOSE_SOURCE: {
		fromReceiver: [null],
		fromSource: ["ACTIVE"],
		toReceiver: null,
		toSource: "CLOSED"
	},
	CONSUME_PENDING_NAVIGATION: {
		fromReceiver: ["PENDING_NAVIGATION"],
		fromSource: ["SUSPENDED", "DEPARTED_TO_WORKSPACE"],
		toReceiver: "INSPECTING",
		toSource: RETAIN
	},
	DEPART_TO_WORKSPACE: {
		fromReceiver: [null, "INSPECTING"],
		fromSource: ["ACTIVE", "SUSPENDED"],
		toReceiver: RETAIN,
		toSource: "DEPARTED_TO_WORKSPACE"
	},
	PREPARE_SOURCE: {
		fromReceiver: [null],
		fromSource: ["ACTIVE"],
		toReceiver: null,
		toSource: "ACTIVE"
	},
	SUSPEND_SOURCE: {
		fromReceiver: [null],
		fromSource: ["ACTIVE"],
		toReceiver: "PENDING_NAVIGATION",
		toSource: "SUSPENDED"
	}
};

export const CHILD_EDITOR_CAP = 2;

export interface CappedFrame {
	countsTowardCap: boolean;
}

export const countedChildFrames = (frames: readonly CappedFrame[]): number =>
	frames.filter((frame) => frame.countsTowardCap).length;

export const exceedsChildEditorCap = (frames: readonly CappedFrame[]): boolean =>
	countedChildFrames(frames) + 1 > CHILD_EDITOR_CAP;

export interface LandingFrame {
	phase: ContinuationFramePhase;
	receiverPhase: ContinuationReceiverPhase | null;
}

const RESUME_LANDING_PHASES: ContinuationFramePhase[] = ["DEPARTED_TO_WORKSPACE", "RESUMING"];

export const resolveReturnLandingIndex = (frames: readonly LandingFrame[]): number => {
	for (let index = frames.length - 1; index >= 0; index -= 1) {
		if (RESUME_LANDING_PHASES.includes(frames[index].phase)) return index;
	}
	for (let index = frames.length - 1; index >= 0; index -= 1) {
		if (frames[index].receiverPhase !== null) return index;
	}
	return -1;
};

export interface ContinuationIdentity {
	accountEmail: string;
	sessionNonce: string;
	tenantId: string;
}

export interface ContinuationFence {
	epoch?: number;
	generation?: number;
	identity?: ContinuationIdentity;
	owner?: string;
}

export const matchesIdentity = (
	candidate: ContinuationIdentity | undefined,
	live: ContinuationIdentity | undefined
): boolean =>
	Boolean(candidate) &&
	Boolean(live) &&
	candidate?.accountEmail === live?.accountEmail &&
	candidate?.tenantId === live?.tenantId &&
	candidate?.sessionNonce === live?.sessionNonce;

export const isStale = (captured: ContinuationFence, live: ContinuationFence): boolean => {
	if (captured.identity !== undefined && !matchesIdentity(captured.identity, live.identity)) return true;
	if (captured.owner !== undefined && captured.owner !== live.owner) return true;
	if (captured.epoch !== undefined && captured.epoch !== live.epoch) return true;
	return captured.generation !== undefined && captured.generation !== live.generation;
};

export const isCleanExactPath = (path: string): boolean =>
	path.startsWith("/") && !path.startsWith("//") && !path.includes("?") && !path.includes("#");

export const isAllowlistedSourcePath = (path: string, allowedPrefixes: readonly string[]): boolean =>
	isCleanExactPath(path) && allowedPrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

const transition = (phases: ContinuationPhases, event: ContinuationEvent): ContinuationPhases | null => {
	const rule = CONTINUATION_TRANSITIONS[event];
	if (!rule.fromSource.includes(phases.sourcePhase)) return null;
	if (!rule.fromReceiver.includes(phases.receiverPhase)) return null;
	return {
		receiverPhase: rule.toReceiver === RETAIN ? phases.receiverPhase : rule.toReceiver,
		sourcePhase: rule.toSource === RETAIN ? phases.sourcePhase : rule.toSource
	};
};

export default transition;
